import { useState, useEffect, useMemo } from "react";
import { Grid, Card, Button, Divider, ClickAwayListener } from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import SuiBox from "components/SuiBox";

import {
  collection,
  getDocs,
  getDoc,
  addDoc,
  query,
  where,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { firestore } from "firebaseConfig";
import ScheduleModal from "./ScheduleModal";
import RecipeModal from "./RecipeModal";
import RecipeDetailModal from "./RecipeDetailModal";
import AllRecipes from "./AllRecipes";
import SuiTypography from "components/SuiTypography";
import SuiButton from "components/SuiButton";
import SideRecipeCard from "./sidebarLayout/SideRecipeCard";
import { useDispatch, useSelector } from "react-redux";
import { deleteDocument } from "store/features/recipes";
import { recipeSelector } from "store/features/recipes";
import { toast } from "react-toastify";
import moment from "moment";
import { AddDiet, EditDietChart } from "services/dietchart";
import { v4 as uuidv4 } from "uuid";
import AddNewSchedule from "./AddSchedule";
import { AddSchedule } from "store/features/recipes";
import RichTextEditor from "components/RichTextEditor";
import { htmlToText } from "html-to-text";
import { cleanHTML } from "utils/common";
import AddedRecipeEdit from "./AddedRecipeEdit";
import ConfirmationModal from "components/ConfirmationModal/ConfirmationModal";
import { defaultAllMealSchedules } from "utils/common";
import AIButton from "./AI/AIButton";

let dietChartObj = {
  earlyMorning: [],
  midMorning: [],
  breakfast: [],
  brunch: [],
  lunch: [],
  midMeal: [],
  evening: [],
  dinner: [],
  lateNight: [],
};

const defaultErrorv1 = {
  name: false,
  description: true,
  preparation: false,
  calories: false,
  foodType: false,
  ingredients: false,
  nutritions: false,
  howToTake: false,
  weight: false,
  foodCategory: false,
  image: false,
  toggleMode: false,
};

const errorDefault = {
  clientName: null,
  date: null,
  notes: null,
  chartType: null,
  weekPlan: null,
};

export default function DietPlannerIndex({
  uid,
  setMode,
  mode,
  selectedItem,
  setSelectedItem,
  setSelectedClientv1,
  formData,
  setFormData,
  setDietChart,
  setSearchParams,
  searchParams,
  dietPlan,
  setDietPlan,
  schedules,
  clientDetailsData,
}) {
  const dispatch = useDispatch();
  const { isFetching, message, isError, isSuccess } =
    useSelector(recipeSelector);

  const paramsMode = searchParams?.get("mode");

  const [scheduleModalOpen, setScheduleModalOpen] = useState(false);
  const [defaultMealSchedules, setDefaultMealSchedules] = useState(
    defaultAllMealSchedules
  );
  const [recipeModalOpen, setRecipeModalOpen] = useState(false);
  const [recipeDetailModalOpen, setRecipeDetailModalOpen] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [customSchedules, setCustomSchedules] = useState([]);
  const [deleteModal, setDeleteModal] = useState(false);
  const [activeSchedule, setActiveSchedule] = useState("earlyMorning");
  const [note, setNote] = useState("");
  const [noteEditorOpen, setNoteEditorOpen] = useState(false);
  const [defaultDietObj, setDefaultDietObj] = useState(false);
  const [selectedDate, setSelectedDate] = useState(null);
  const [chartError, setChartError] = useState({
    earlyMorning: [defaultErrorv1],
    breakfast: [defaultErrorv1],
    midMorning: [defaultErrorv1],
    brunch: [defaultErrorv1],
    midMeal: [defaultErrorv1],
    lunch: [defaultErrorv1],
    postLunch: [defaultErrorv1],
    evening: [defaultErrorv1],
    beforeDinner: [defaultErrorv1],
    dinner: [defaultErrorv1],
    postDinner: [defaultErrorv1],
    lateNight: [defaultErrorv1],
  });
  const [errorMessage, setErrorMessage] = useState(errorDefault);
  const [loader, setLoader] = useState(false);

  const schedulesCollection = collection(firestore, "customSchedules");

  const fetchCustomSchedules = async () => {
    try {
      const q = query(schedulesCollection, where("adminId", "==", uid));
      const querySnapshot = await getDocs(q);

      const schedulesData = [];
      const updatedDietPlan = { ...dietChartObj };

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        schedulesData.push({ id: doc.id, ...data });

        if (!updatedDietPlan[data.normalizedId]) {
          updatedDietPlan[data.normalizedId] = [];
        }
      });

      setCustomSchedules(schedulesData);
    } catch (error) {
      console.error("Error fetching schedules:", error);
    }
  };

  useEffect(() => {
    fetchCustomSchedules();
  }, []);

  useEffect(() => {
    if (formData?.notes) {
      setNote(formData.notes);
    }
  }, [formData]);

  useEffect(() => {
    if (schedules.length > 0) {
      const dynamicDietChartObj = {};

      schedules.forEach((schedule) => {
        const key = schedule.normalizedId || schedule.id;
        dynamicDietChartObj[key] = [];
      });

      setDefaultDietObj(dynamicDietChartObj);
    }
  }, [schedules]);

  useEffect(() => {
    if (isSuccess) {
      setDeleteModal(false);
      setRecipeDetailModalOpen(false);
      setSelectedItem(null);
    }

    if (isError && message) {
      toast.error(message);
    }
  }, [isSuccess, isError, message]);

  const getScheduleTime = (scheduleId) => {
    const defaultSchedule = defaultMealSchedules.find(
      (s) => s.id === scheduleId
    );
    if (defaultSchedule) return defaultSchedule.time;

    const customSchedule = customSchedules.find(
      (s) => s.normalizedId === scheduleId
    );
    if (customSchedule) return customSchedule.time;

    return null;
  };

  const doesScheduleTimeExist = (time) => {
    const defaultSchedule = defaultMealSchedules.find((s) => s.time === time);
    if (defaultSchedule) return true;

    const customSchedule = customSchedules.find((s) => s.time === time);
    if (customSchedule) return true;

    return false;
  };

  const handleAddRecipe = (recipe) => {
    setDietPlan((prev) => {
      const updatedSchedule = prev[activeSchedule] || [];

      const isDuplicate = updatedSchedule.some((r) => r.id === recipe.id);

      if (isDuplicate) {
        toast.error(`${recipe.name} is already added to ${activeSchedule}.`);
        return prev;
      }

      const scheduleTime = getScheduleTime(activeSchedule);

      const recipeWithTime = {
        ...recipe,
        instanceId: recipe.instanceId || uuidv4(),
        time: scheduleTime,
        toggleMode: "OR",
      };

      return {
        ...prev,
        [activeSchedule]: [...updatedSchedule, recipeWithTime],
      };
    });
    setRecipeDetailModalOpen(false);
  };

  const handleRecipeCardClick = (recipe) => {
    setSelectedRecipe(recipe);
    setRecipeDetailModalOpen(true);
  };

  const totalCalories = useMemo(() => {
    let total = 0;
    if (!dietPlan || typeof dietPlan !== "object") return total;
    Object.values(dietPlan).forEach((recipes) => {
      if (Array.isArray(recipes)) {
        recipes.forEach((recipe) => {
          let calories = recipe.calories;

          if (typeof calories === "string") {
            calories = parseFloat(calories);
          }

          if (!isNaN(calories)) {
            total += calories;
          }
        });
      }
    });

    return total;
  }, [dietPlan]);

  const handleAddSchedule = async (scheduleData) => {
    try {
      const normalizedId = scheduleData.title
        .toLowerCase()
        .replace(/[^a-zA-Z\s]/g, "")
        .split(" ")
        .map((word, index) =>
          index === 0
            ? word.toLowerCase()
            : word.charAt(0).toUpperCase() + word.slice(1)
        )
        .join("");

      let normalizedTime = scheduleData.time;
      if (scheduleData.time instanceof Date) {
        const hours = scheduleData.time.getHours().toString().padStart(2, "0");
        const minutes = scheduleData.time
          .getMinutes()
          .toString()
          .padStart(2, "0");
        normalizedTime = `${hours}:${minutes}`;
      } else if (
        typeof scheduleData.time === "string" &&
        scheduleData.time.match(/^\d{2}:\d{2}$/) === null
      ) {
        throw new Error("Invalid time format. Please use HH:mm");
      }

      const result = await dispatch(
        AddSchedule({
          formdata: {
            ...scheduleData,
            normalizedId,
            time: normalizedTime,
          },
          uid: uid,
        })
      ).unwrap();

      setCustomSchedules((prev) => [
        ...prev,
        {
          id: normalizedId,
          title: scheduleData.title,
          time: normalizedTime,
          icon: result.icon || "",
          ...result,
        },
      ]);

      setDietPlan((prev) => ({
        ...prev,
        [normalizedId]: [],
      }));

      setScheduleModalOpen(false);
      toast.success("Schedule added successfully!");
    } catch (error) {
      console.error("Error adding schedule:", error);
      toast.error(error?.message || "Failed to add schedule");
    }
  };

  const handleUpdateTime = (scheduleKey, newTime) => {
    let normalizedTime = newTime;
    if (
      typeof newTime === "string" &&
      newTime.match(/^\d{2}:\d{2}$/) === null
    ) {
      toast.error("Invalid time format. Please use HH:mm");
      return;
    }

    const isCustomSchedule = customSchedules.some(
      (s) => s.normalizedId === scheduleKey
    );

    if (isCustomSchedule) {
      setCustomSchedules((prev) =>
        prev.map((schedule) =>
          schedule.normalizedId === scheduleKey
            ? { ...schedule, time: normalizedTime }
            : schedule
        )
      );
      return;
    }

    const isDefaultSchedule = defaultMealSchedules.some(
      (s) => s.id === scheduleKey
    );

    if (isDefaultSchedule) {
      setDefaultMealSchedules((prev) =>
        prev.map((schedule) =>
          schedule.id === scheduleKey
            ? { ...schedule, time: normalizedTime }
            : schedule
        )
      );
      return;
    }
  };

  const deleteHandler = async () => {
    dispatch(deleteDocument({ id: selectedItem }));
  };

  const onRecipeEdit = async (editedRecipe) => {
    setRecipeDetailModalOpen(false);
    setMode("edit");
    setRecipeModalOpen(true);
    if (editedRecipe?.id) {
      try {
        const recipeRef = doc(firestore, "recipe", editedRecipe.id);
        const snapshot = await getDoc(recipeRef);
        const latestRecipe = snapshot.exists()
          ? { id: snapshot.id, ...snapshot.data() }
          : editedRecipe;
        setSelectedItem({
          ...latestRecipe,
          time: editedRecipe.time,
          toggleMode: editedRecipe.toggleMode ?? "OR",
        });
      } catch {
        setSelectedItem(editedRecipe);
      }
    } else {
      setSelectedItem(editedRecipe);
    }
  };

  const onDeleteSchedule = async (e, scheduleKey) => {
    e.stopPropagation();
    if (!dietPlan || typeof dietPlan !== "object") return;
    if (Object.keys(dietPlan).length <= 1) {
      toast.warning("At least one schedule must remain.");
      return;
    }

    try {
      const customSchedule = customSchedules.find(
        (s) => s.normalizedId === scheduleKey
      );

      if (customSchedule) {
        const docRef = doc(schedulesCollection, customSchedule.id);
        await deleteDoc(docRef);

        setCustomSchedules((prev) =>
          prev.filter((s) => s.normalizedId !== scheduleKey)
        );

        toast.success(`Deleted "${customSchedule.title}" from schedules`);
      }

      const updatedPlan = { ...dietPlan };
      delete updatedPlan[scheduleKey];
      setDietPlan(updatedPlan);
    } catch (err) {
      console.error("Error deleting schedule:", err);
      toast.error("Failed to delete schedule. Please try again.");
    }
  };

  const onSaveHandler = async (e) => {
    if (!dietPlan || typeof dietPlan !== "object") return;
    let keys = Object.keys(dietPlan);

    if (!note || note.trim() === "") {
      toast.error("Add a Note to Proceed");
      return;
    }
    for (let i = 0; i < keys.length; i++) {
      if (
        dietPlan[keys[i]] &&
        keys[i] !== "from" &&
        keys[i] !== "to" &&
        keys[i] !== "notes" &&
        keys[i] !== "id" &&
        keys[i] !== "selected" &&
        keys[i] !== "date" &&
        keys[i] !== "clientName" &&
        keys[i] !== "dietChartId" &&
        keys[i] !== "clientId" &&
        keys[i] !== "createdAt" &&
        keys[i] !== "weekPlan" &&
        keys[i] !== "chartType"
      ) {
        let recipe = dietPlan[keys[i]];
        let recipeId = keys[i];
        const recipeIds = schedules.map(
          (schedule) => schedule.normalizedId || schedule.id
        );

        const recipeIds1 = schedules.map((schedule) => schedule.title);

        for (let j = 0; j < recipe.length; j++) {
          if (
            recipe[j].name ||
            recipe[j].description ||
            recipe[j].preparation ||
            recipe[j].howToTake ||
            recipe[j].nutritions ||
            recipe[j].ingredients ||
            recipe[j].foodType ||
            recipe[j].calories ||
            recipe[j].weight ||
            recipe[j].foodCategory ||
            recipe[j].unit ||
            recipe[j].qty
          ) {
            if (!recipe[j].name && recipeId === recipeIds[i]) {
              toast.error(
                `Please enter a Recipe Name field for ${recipeIds1[i]}`
              );
              setChartError({
                ...chartError,
                [recipeId]: {
                  name: true,
                },
              });
              return;
            }
            if (!recipe[j].qty && recipeId === recipeIds[i]) {
              toast.error(`Please enter Qty. field for ${recipeIds1[i]}`);
              setChartError({
                ...errorMessage,
                [recipeId]: {
                  qty: true,
                },
              });
              return;
            }
            if (!recipe[j].unit && recipeId === recipeIds[i]) {
              toast.error(`Please fill Unit field for ${recipeIds1[i]}`);
              setChartError({
                ...errorMessage,
                [recipeId]: {
                  unit: true,
                },
              });
              return;
            }
            if (!recipe[j].foodCategory && recipeId === recipeIds[i]) {
              toast.error(
                `Please fill Food Category field for ${recipeIds1[i]}`
              );
              setChartError({
                ...errorMessage,
                [recipeId]: {
                  foodCategory: true,
                },
              });
              return;
            }
            if (!recipe[j].time && recipeId === recipeIds[i]) {
              toast.error(`Please fill Time field for ${recipeIds1[i]}`);
              setChartError({
                ...errorMessage,
                [recipeId]: {
                  time: true,
                },
              });
              return;
            }
          } else if (recipeId === recipeIds[i]) {
            toast.error(
              `Please fill all added recipe fields for ${recipeIds1[i]}`
            );
            setChartError({
              ...errorMessage,
              [recipeId]: {
                name: true,
                description: true,
                preparation: true,
                calories: true,
                foodType: true,
                ingredients: true,
                nutritions: true,
                howToTake: true,
                weight: true,
                foodCategory: true,
                // toggleType:true,
                toggleMode: true,
                image: true,
              },
            });
            return;
          }
        }
      }
    }
    try {
      setLoader(true);
      // Step 1: Normalize all recipe fields before saving
      function normalizeRecipeFields(recipe) {
        return recipe.map((item) => ({
          ...item,
          name: item.name?.value || item.name || "",
          unit: item.unit?.value || item.unit || "",
          foodCategory: Array.isArray(item.foodCategory)
            ? item.foodCategory.map((fc) => fc.value || fc).filter(Boolean)
            : [item.foodCategory?.value || item.foodCategory].filter(Boolean),
          andRecipe: item.andRecipe?.value || item.andRecipe || "",
          alternativeName:
            item.alternativeName?.value || item.alternativeName || "",
          groceryList: Array.isArray(item.groceryList) ? item.groceryList : [],
        }));
      }
      const normalizedPlan = { ...dietPlan };

      const mealTypes = schedules.map(
        (schedule) => schedule.normalizedId || schedule.id
      );

      mealTypes.forEach((meal) => {
        if (Array.isArray(normalizedPlan[meal])) {
          normalizedPlan[meal] = normalizeRecipeFields(normalizedPlan[meal]);
        }
      });

      if (dietPlan?.dietChartId && mode === "edit") {
        const updatedDiet = await EditDietChart({
          formData,
          dietPlan: normalizedPlan,
          uid: uid,
        });
        setDietChart((prevDietCharts) =>
          prevDietCharts.map((item) =>
            item.dietChartId === updatedDiet.dietChartId ? updatedDiet : item
          )
        );
        toast.success("Diet updated successfully!!");
      } else {
        const commonChartId = uuidv4();
        const clientDietPlan = {
          ...normalizedPlan,
          ...formData,
          date: new Date(dietPlan.from),
          createdAt: new Date(),
          clientName: formData.clientName,
          clientId: formData.clientName.key,
          dietChartId: commonChartId,
          notes: note,
        };
        await AddDiet({
          formdata: formData,
          dietPlan: clientDietPlan,
          uid: uid,
        });
        setSelectedClientv1(formData.clientName);
        toast.success("Diet added successfully!!");
      }
      setLoader(false);
      setTimeout(() => {
        setNote("");
        setSelectedDate(null);
        if (selectedItem) {
          setSelectedItem(null);
        }
        setDietPlan({ ...defaultDietObj });
        setErrorMessage({ ...errorDefault });

        const newParams = new URLSearchParams(searchParams);
        newParams.delete("mode");
        newParams.delete("recipeId");
        setSearchParams(newParams);
      }, 500);
    } catch (e) {
      console.log({ e });
      setLoader(false);
      toast.error("Something went wrong, retry!!");
    }
  };

  return (
    <SuiBox>
      <SuiBox>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={8} md={9} lg={9}>
            <AddNewSchedule
              dietPlan={dietPlan}
              defaultMealSchedules={defaultMealSchedules}
              setDefaultMealSchedules={setDefaultMealSchedules}
              customSchedules={customSchedules}
              setCustomSchedules={setCustomSchedules}
              setActiveSchedule={setActiveSchedule}
              onDeleteSchedule={onDeleteSchedule}
              activeSchedule={activeSchedule}
              setScheduleModalOpen={setScheduleModalOpen}
              onUpdateTime={handleUpdateTime}
            />

            {paramsMode !== "edit_recipe" && (
              <AllRecipes
                uid={uid}
                setRecipeModalOpen={setRecipeModalOpen}
                handleRecipeCardClick={handleRecipeCardClick}
                setDietPlan={setDietPlan}
                dietPlan={dietPlan}
                handleAddRecipe={handleAddRecipe}
                setMode={setMode}
                setSelectedItem={setSelectedItem}
                activeSchedule={activeSchedule}
                defaultDietObj={defaultDietObj}
              />
            )}

            {paramsMode == "edit_recipe" && (
              <AddedRecipeEdit
                selectedItem={selectedItem}
                setSelectedItem={setSelectedItem}
                searchParams={searchParams}
                setSearchParams={setSearchParams}
                setDietPlan={setDietPlan}
                dietPlan={dietPlan}
                setMode={setMode}
                uid={uid}
              />
            )}
          </Grid>

          <Grid item xs={12} sm={4} md={3} lg={3}>
            <Card
              sx={{
                px: 1,
                py: 3,
                borderRadius: "10px",
                display: "flex",
                flexDirection: "column",
                overflow: "hidden",
                height: 852,
              }}
              elevation={4}
            >
              <SuiBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  px: 0.5,
                }}
              >
                <SuiTypography
                  variant="body2"
                  sx={{
                    fontSize: "15px",
                    fontWeight: "600",
                  }}
                >
                  Added Recipes
                </SuiTypography>

                <SuiButton
                  component={Button}
                  color="dark"
                  variant="gradient"
                  onClick={onSaveHandler}
                  size="small"
                  sx={{
                    px: 1.2,
                    whiteSpace: "nowrap",
                  }}
                  loader={isFetching}
                  disabled={
                    !dietPlan ||
                    typeof dietPlan !== "object" ||
                    Object.values(dietPlan)
                      .filter(Array.isArray)
                      .every((recipes) => recipes.length === 0)
                  }
                >
                  Save Plan
                </SuiButton>
              </SuiBox>
              <SuiBox
                sx={{
                  mt: 2,
                  position: "relative",
                  width: "100%",
                  p: 0.5,
                  border: "1px dashed #ccc",
                  borderRadius: "8px",
                  transition: "background-color 0.2s ease",
                  "&:hover": {
                    backgroundColor: "#f9f9f9",
                  },
                }}
              >
                {noteEditorOpen ? (
                  <ClickAwayListener
                    onClickAway={() => {
                      const cleaned = cleanHTML(note);

                      if (cleaned === "") {
                        setNote("");
                      } else {
                        setNote(cleaned);
                      }

                      setNoteEditorOpen(false);
                    }}
                  >
                    <SuiBox>
                      <RichTextEditor
                        name="notes"
                        value={note}
                        onChange={({ target }) => {
                          let { value } = target;

                          value = cleanHTML(value);

                          setNote(value);
                          setErrorMessage((prev) => ({
                            ...prev,
                            notes: false,
                          }));
                        }}
                        toolbar={true}
                        height={95}
                        borderRadius="10px"
                        autoFocus
                      />
                    </SuiBox>
                  </ClickAwayListener>
                ) : (
                  <SuiBox
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      cursor: "pointer",
                      "&:hover .addNoteBtn": { opacity: 1 },
                    }}
                    onMouseEnter={() => setNoteEditorOpen(true)}
                  >
                    <SuiTypography
                      variant="body2"
                      color={note ? "text.primary" : "text.secondary"}
                      sx={{}}
                    >
                      {htmlToText(
                        note
                          ? note.length > 30
                            ? `${note.slice(0, 28)}...`
                            : note
                          : "No notes added yet."
                      )}
                    </SuiTypography>
                  </SuiBox>
                )}
              </SuiBox>
              <Divider />
              <SuiBox
                sx={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: "8px",
                  px: 0.5,
                  py: 0.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  boxShadow: "sm",
                  mb: 2,
                }}
              >
                <SuiTypography
                  variant="body2"
                  sx={{
                    fontWeight: "600",
                    fontSize: "14px",
                    color: "#344767",
                  }}
                >
                  🔥 Total Calories
                </SuiTypography>

                <SuiTypography
                  variant="body2"
                  sx={{
                    fontWeight: "700",
                    color: "#f96800",
                  }}
                >
                  {totalCalories?.toFixed(2)} kcal
                </SuiTypography>
              </SuiBox>

              <SuiBox
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  px: 0.5,
                  mt: 2,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                }}
              >
                {!dietPlan ||
                typeof dietPlan !== "object" ||
                Object.values(dietPlan)
                  .filter(Array.isArray)
                  .every((recipes) => recipes.length === 0) ? (
                  <SuiTypography
                    variant="body2"
                    sx={{ textAlign: "center", color: "text.secondary", mt: 4 }}
                  >
                    Please add at least one recipe to save the plan.
                  </SuiTypography>
                ) : (
                  Object.entries(dietPlan)
                    .filter(([, recipes]) => Array.isArray(recipes))
                    .sort(([a], [b]) => {
                      const findTime = (scheduleId) => {
                        const defaultTime = defaultMealSchedules.find(
                          (s) => s.id === scheduleId
                        )?.time;

                        const customTime = customSchedules.find(
                          (s) => s.normalizedId === scheduleId
                        )?.time;

                        return defaultTime || customTime || "00:00";
                      };

                      const timeA = findTime(a);
                      const timeB = findTime(b);

                      return moment(timeA, "HH:mm").diff(
                        moment(timeB, "HH:mm")
                      );
                    })
                    .filter(([timeSlot, recipes]) => recipes.length > 0)
                    .map(([timeSlot, recipes]) => {
                      const findTime = (scheduleId) => {
                        const defaultTime = defaultMealSchedules.find(
                          (s) => s.id === scheduleId
                        )?.time;

                        const customTime = customSchedules.find(
                          (s) => s.normalizedId === scheduleId
                        )?.time;

                        return defaultTime || customTime || "00:00";
                      };

                      const time = findTime(timeSlot);
                      const formattedTime = moment(time, "HH:mm").format(
                        "hh:mm A"
                      );

                      return (
                        <SuiBox key={timeSlot} sx={{ mb: 3, width: "100%" }}>
                          <SuiBox
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 1.5,
                              mb: 1,
                            }}
                          >
                            <SuiTypography
                              variant="button"
                              sx={{
                                textTransform: "capitalize",
                                fontWeight: 600,
                                fontSize: "15px",
                              }}
                            >
                              {timeSlot.replace(/([A-Z])/g, " $1")}
                            </SuiTypography>
                            <SuiBox
                              sx={{
                                bgcolor: "#f96800",
                                color: "#fff",
                                px: 1.2,
                                py: 0.4,
                                borderRadius: 1,
                                fontSize: "12px",
                                fontWeight: "bold",
                                minWidth: "70px",
                                textAlign: "center",
                              }}
                            >
                              {formattedTime}
                            </SuiBox>
                          </SuiBox>

                          <SuiBox
                            sx={{
                              display: "flex",
                              flexDirection: "column",
                              gap: 1,
                            }}
                          >
                            {recipes.map((recipe, recipeIndex) => (
                              <SideRecipeCard
                                setSelectedItem={setSelectedItem}
                                key={
                                  recipe.instanceId ||
                                  `${timeSlot}-${recipe.id}-${recipeIndex}`
                                }
                                recipe={recipe}
                                scheduleKey={timeSlot}
                                recipeIndex={recipeIndex}
                                onDelete={(index) => {
                                  setDietPlan((prev) => ({
                                    ...prev,
                                    [timeSlot]: prev[timeSlot].filter(
                                      (_, idx) => idx !== index
                                    ),
                                  }));
                                }}
                                onToggleMode={(index, newToggleMode) => {
                                  setDietPlan((prev) => ({
                                    ...prev,
                                    [timeSlot]: prev[timeSlot].map((r, idx) =>
                                      idx === index
                                        ? { ...r, toggleMode: newToggleMode }
                                        : r
                                    ),
                                  }));
                                }}
                                setSearchParams={setSearchParams}
                                searchParams={searchParams}
                              />
                            ))}
                          </SuiBox>
                        </SuiBox>
                      );
                    })
                )}
              </SuiBox>
            </Card>
          </Grid>
        </Grid>
        <AIButton
          clientDetailsData={clientDetailsData}
          foodPreference={formData?.clientName?.preference ?? ""}
          setDietPlan={setDietPlan}
        />
      </SuiBox>

      <ScheduleModal
        open={scheduleModalOpen}
        onClose={() => setScheduleModalOpen(false)}
        onSave={handleAddSchedule}
        doesScheduleTimeExist={doesScheduleTimeExist}
      />

      <RecipeModal
        open={recipeModalOpen}
        onClose={() => setRecipeModalOpen(false)}
        selectedItem={selectedItem}
        mode={mode}
        setMode={setMode}
        defaultMealSchedules={defaultAllMealSchedules}
        setSelectedItem={setSelectedItem}
        customSchedules={customSchedules}
      />

      <RecipeDetailModal
        open={recipeDetailModalOpen}
        onClose={() => setRecipeDetailModalOpen(false)}
        recipe={selectedRecipe}
        onAdd={handleAddRecipe}
        onEdit={(recipe) => {
          onRecipeEdit(recipe);
        }}
        onDelete={(recipeId) => {
          setDeleteModal(true);
          setSelectedItem(recipeId);
        }}
      />

      {deleteModal && (
        <ConfirmationModal
          isFetching={isFetching}
          title={`Are you sure? This will permanently delete the recipe: '${selectedRecipe.name}'.`}
          onCancelHandler={() => setDeleteModal(false)}
          onConfirmHandler={deleteHandler}
        />
      )}
    </SuiBox>
  );
}
